import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import AgeGate from './AgeGate';
import CampaignLanding from './CampaignLanding';
import CampaignForm from './CampaignForm';
import CampaignConfirm from './CampaignConfirm';
import ReferralInvite from './ReferralInvite';
import CampaignFooter from './CampaignFooter';
import { captureUtm } from './tracking';
import './campaign.css';

const AGE_GATE_KEY = 'ff_lokal_age_confirmed';
const CANONICAL_HOST = process.env.REACT_APP_CANONICAL_HOST || 'https://corp.thelokal.ca';

const META = {
  title: 'Corporate & Custom Wine | The Lokal Winery',
  description:
    'Your Wine. Your Label. Your Moment. Custom wine and personalized labels for client gifts, employee appreciation, Airbnb welcome bottles, weddings and events. Save 15% on your first Corporate & Custom Wine order. Made in Fenelon Falls, Ontario.',
  image: 'https://images.unsplash.com/photo-1560493676-04071c5f467b?auto=format&fit=crop&w=1200&q=80',
};

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute('content', content);
};

const applyMetadata = (pathname) => {
  const canonical = `${CANONICAL_HOST}${pathname === '/' ? '/' : pathname}`;
  document.title = META.title;
  setMeta('name', 'description', META.description);
  setMeta('property', 'og:title', META.title);
  setMeta('property', 'og:description', META.description);
  setMeta('property', 'og:type', 'website');
  setMeta('property', 'og:url', canonical);
  setMeta('property', 'og:image', META.image);
  setMeta('property', 'og:site_name', 'The Lokal Winery');
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', META.title);
  setMeta('name', 'twitter:description', META.description);
  setMeta('name', 'twitter:image', META.image);
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link); }
  link.setAttribute('href', canonical);
};

const CampaignApp = ({ basePath = '' }) => {
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [ageChecked, setAgeChecked] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Legacy ff.thelokal.ca → corp.thelokal.ca (preserve path + UTMs)
    if (/^ff\./i.test(window.location.hostname)) {
      window.location.replace(`${CANONICAL_HOST}${window.location.pathname}${window.location.search}`);
      return;
    }
    captureUtm();
    const stored = sessionStorage.getItem(AGE_GATE_KEY);
    if (stored === 'yes') setAgeConfirmed(true);
    setAgeChecked(true);
  }, []);

  useEffect(() => {
    // Legacy /ff/* path alias → canonical /corp/* (same host)
    if (basePath === '/ff') {
      const rest = location.pathname.replace(/^\/ff/, '') || '/';
      navigate(`/corp${rest}${location.search}`, { replace: true });
      return;
    }
    window.scrollTo(0, 0);
    const rel = basePath ? location.pathname.replace(basePath, '') || '/' : location.pathname;
    applyMetadata(rel);
  }, [location.pathname, location.search, basePath, navigate]);

  if (!ageChecked) return null;

  if (!ageConfirmed) {
    return (
      <AgeGate
        onConfirm={() => {
          sessionStorage.setItem(AGE_GATE_KEY, 'yes');
          setAgeConfirmed(true);
        }}
      />
    );
  }

  return (
    <div data-testid="campaign-app" className="ff-root">
      <Routes>
        <Route path="/" element={<CampaignLanding basePath={basePath} />} />
        <Route path="/order" element={<CampaignForm basePath={basePath} />} />
        <Route path="/thank-you" element={<CampaignConfirm basePath={basePath} />} />
        <Route path="/r/:code" element={<ReferralInvite basePath={basePath} />} />
      </Routes>
      <CampaignFooter />
    </div>
  );
};

export default CampaignApp;
